import * as Guacamole from '@glokon/guacamole-common-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GUACAMOLE_CLIENT_CONTAINER_ID } from '../../constants/vms';

export enum GuacamoleClientState {
  IDLE = 0,
  CONNECTING = 1,
  WAITING = 2,
  CONNECTED = 3,
  DISCONNECTING = 4,
  DISCONNECTED = 5,
}

interface ConnectionState {
  isAcknowledged: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  websocketFailed: boolean;
  error: string | null;
  sessionStatus: GuacamoleClientState;
}

type WebSocketUrlResult =
  | {
      success: true;
      url: string;
      parameters?: string;
    }
  | {
      success: false;
      error: string;
    };

const MAX_RETRY_ATTEMPTS = 10;
const HEARTBEAT_INTERVAL = 2_000;
const CONNECTION_TIMEOUT = 15_000;

export const useGuacamoleConnection = (remoteUrl: string, onSuccessfulConnection?: () => void) => {
  const [state, setState] = useState<ConnectionState>({
    isConnected: false,
    isConnecting: false,
    isAcknowledged: false,
    websocketFailed: false,
    error: null,
    sessionStatus: GuacamoleClientState.IDLE,
  });

  // Core connection
  const guacClientRef = useRef<Guacamole.Client | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Retry and reconnection
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef<number>(0);
  const lastRetryReasonRef = useRef<string>('');

  // Heartbeat monitoring
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const connectionHealthTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tab activity tracking
  const isTabActiveRef = useRef<boolean>(
    typeof window !== 'undefined' && typeof document !== 'undefined'
      ? !document.hidden && (typeof document.hasFocus === 'function' ? document.hasFocus() : true)
      : false,
  );
  const pendingReconnectRef = useRef<boolean>(false);

  const mainStateRef = useRef(state);
  const isReconnectingRef = useRef(false);
  const triggerReconnectionRef = useRef<((reason?: string, immediate?: boolean) => Promise<void>) | null>(null);
  const connectionFunctionRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    mainStateRef.current = state;
  }, [state]);

  // Extract WebSocket URL and connection parameters for Guacamole tunnel
  const extractWebSocketUrl = useCallback((): WebSocketUrlResult => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      let timezone = 'UTC';
      try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      } catch (error) {
        timezone = 'UTC';
      }

      const pixelDensity = window.devicePixelRatio || 1;
      let defaultWidth = 1920;
      let defaultHeight = 1080;
      const dpi = Math.floor(96 * pixelDensity);

      const containerElement = document.getElementById(GUACAMOLE_CLIENT_CONTAINER_ID);
      if (containerElement) {
        const rect = containerElement.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          defaultWidth = Math.floor(rect.width * pixelDensity);
          defaultHeight = Math.floor(rect.height * pixelDensity);
        }
      }

      const guacLiteUrl = remoteUrl;
      const tunnelUrlObject = new URL(guacLiteUrl);
      tunnelUrlObject.protocol = 'wss';

      const token = tunnelUrlObject.searchParams.get('token');

      if (!token) {
        throw new Error('Token not found');
      }

      const searchParams = new URLSearchParams();
      searchParams.set('token', token);
      searchParams.set('GUAC_TIMEZONE', timezone);
      searchParams.set('GUAC_DATA_SOURCE', 'json');
      searchParams.set('GUAC_TYPE', 'c');
      searchParams.set('GUAC_WIDTH', defaultWidth.toString());
      searchParams.set('GUAC_HEIGHT', defaultHeight.toString());
      searchParams.set('GUAC_DPI', dpi.toString());

      return {
        success: true,
        url: tunnelUrlObject.origin.toString(),
        parameters: searchParams.toString(),
      };
    } catch (error) {
      console.error('error', error);
      if (error instanceof Error && error.name === 'AbortError') {
        return { success: false, error: 'Request aborted' };
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error extracting WebSocket URL',
      };
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [remoteUrl]);

  // Retry WebSocket URL extraction with exponential backoff
  const attemptWebSocketExtraction = useCallback(async (): Promise<WebSocketUrlResult> => {
    const maxRetries = 3;
    let lastError = '';

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await extractWebSocketUrl();
        if (result.success) {
          return result;
        }

        lastError = result.error;

        // Don't retry if request was aborted
        if (result.error === 'Request aborted') {
          return result;
        }

        // Wait before retrying (exponential backoff: 1s, 2s, 3s)
        if (attempt < maxRetries) {
          const base = Math.min(1000 * attempt, 3000);
          const jitter = Math.floor(Math.random() * 250);
          const delay = Math.min(base + jitter, 3000);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        lastError = errorMessage;

        if (error instanceof Error && error.name === 'AbortError') {
          return { success: false, error: 'Request aborted' };
        }

        // Wait before retrying (exponential backoff: 1s, 2s, 3s)
        if (attempt < maxRetries) {
          const base = Math.min(1000 * attempt, 3000);
          const jitter = Math.floor(Math.random() * 250);
          const delay = Math.min(base + jitter, 3000);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    return { success: false, error: `Failed after ${maxRetries} attempts: ${lastError}` };
  }, [extractWebSocketUrl]);

  // Update last activity timestamp for connection health monitoring
  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Format error objects for consistent logging
  const formatError = useCallback((error: unknown): string => {
    if (error === null || error === undefined) {
      return 'null/undefined error';
    }

    // Handle Guacamole Status objects
    if (typeof error === 'object' && 'code' in error && 'message' in error) {
      const statusObj = error as { code: number; message: string };
      return `Status {code: ${statusObj.code}, message: "${statusObj.message}"}`;
    }

    // Handle Error objects
    if (error instanceof Error) {
      return `${error.name}: ${error.message}`;
    }

    // Try JSON stringify for objects
    if (typeof error === 'object') {
      try {
        return JSON.stringify(error);
      } catch {
        return `[object ${error.constructor?.name || 'Object'}]`;
      }
    }

    // Fallback to string conversion
    return String(error);
  }, []);

  // Check if we should retry connection based on attempt count
  const shouldRetry = useCallback((): boolean => {
    return retryCountRef.current < MAX_RETRY_ATTEMPTS;
  }, []);

  // Check connection health by monitoring activity timestamps
  const checkConnectionHealth = useCallback(() => {
    if (!guacClientRef.current || !mainStateRef.current.isConnected) {
      return;
    }

    const timeSinceLastActivity = Date.now() - lastActivityRef.current;

    // Trigger reconnection if no activity detected for too long
    if (timeSinceLastActivity > CONNECTION_TIMEOUT) {
      const errorReason = `Connection health check failed - no activity for ${timeSinceLastActivity}ms`;
      if (triggerReconnectionRef.current) {
        triggerReconnectionRef.current(errorReason, true);
      }
    }
  }, []);

  // Start periodic health monitoring for the connection
  const startHeartbeatMonitoring = useCallback(() => {
    updateLastActivity();

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    heartbeatIntervalRef.current = setInterval(checkConnectionHealth, HEARTBEAT_INTERVAL);
  }, [checkConnectionHealth, updateLastActivity]);

  // Stop health monitoring and clean up intervals
  const stopHeartbeatMonitoring = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (connectionHealthTimeoutRef.current) {
      clearTimeout(connectionHealthTimeoutRef.current);
      connectionHealthTimeoutRef.current = null;
    }
  }, []);

  const triggerReconnection = useCallback(
    async (reason?: string, immediate = false) => {
      if (isReconnectingRef.current) {
        return;
      }

      if (reason) {
        lastRetryReasonRef.current = reason;
      }

      if (!immediate && !shouldRetry()) {
        setState((prev) => ({
          ...prev,
          websocketFailed: true,
          error: 'Connection failed after 3 attempts',
          isConnecting: false,
          sessionStatus: GuacamoleClientState.DISCONNECTED,
        }));
        return;
      }

      // Clean up existing connection
      if (guacClientRef.current) {
        try {
          const client = guacClientRef.current;
          client.onstatechange = null;
          client.onerror = null;
          client.disconnect();
        } catch (error) {}
        guacClientRef.current = null;
      }

      stopHeartbeatMonitoring();

      setState((prev) => ({
        ...prev,
        isConnected: false,
        isConnecting: true,
        websocketFailed: false,
        error: null,
        sessionStatus: GuacamoleClientState.CONNECTING,
      }));

      isReconnectingRef.current = true;

      if (!immediate) {
        retryCountRef.current += 1;
      }

      try {
        if (immediate) {
          if (connectionFunctionRef.current) {
            await connectionFunctionRef.current();
          }
        } else {
          const base = Math.min(1000 * retryCountRef.current, 3000);
          const jitter = Math.floor(Math.random() * 250);
          const delay = base + jitter;

          await new Promise((resolve) => {
            retryTimeoutRef.current = setTimeout(resolve, delay);
          });

          if (connectionFunctionRef.current) {
            await connectionFunctionRef.current();
          }
        }
      } finally {
        isReconnectingRef.current = false;
      }
    },
    [shouldRetry, stopHeartbeatMonitoring],
  );

  useEffect(() => {
    triggerReconnectionRef.current = triggerReconnection;
  }, [triggerReconnection]);

  // Set up Guacamole client with event handlers for connection lifecycle management
  const setupGuacamoleClient = useCallback(
    async (tunnel: Guacamole.Tunnel, parameters?: string): Promise<{ connected: boolean; isAcknowledged: boolean }> => {
      let isAcknowledged = false;
      let isConnected = false;

      let resolveAckWait: (() => void) | null = null;
      let ackTimeoutId: NodeJS.Timeout | null = null;

      let resolveConnected: (() => void) | null = null;
      let rejectConnected: ((reason: string) => void) | null = null;
      const ackWaitPromise = new Promise<void>((resolve) => {
        resolveAckWait = resolve;
        ackTimeoutId = setTimeout(resolve, CONNECTION_TIMEOUT);
      });

      const connectionPromise = new Promise<void>((resolve, reject) => {
        resolveConnected = resolve;
        rejectConnected = reject;
        setTimeout(() => reject('Connection timeout'), CONNECTION_TIMEOUT);
      });

      const originalSendMessage = tunnel.sendMessage.bind(tunnel);
      // biome-ignore lint/suspicious/noExplicitAny: Type is derived from Guacamole.Tunnel
      tunnel.sendMessage = (...elements: any[]) => {
        if (!isAcknowledged && elements[0] === 'ack') {
          isAcknowledged = true;
          if (ackTimeoutId) clearTimeout(ackTimeoutId);
          if (resolveAckWait) resolveAckWait();
        }
        return originalSendMessage(...elements);
      };

      const client = new Guacamole.Client(tunnel);
      let resolved = false;
      // Clean up event handlers to prevent memory leaks
      const cleanup = () => {
        if (!resolved) {
          resolved = true;
          client.onstatechange = null;
          client.onerror = null;
          tunnel.onerror = null;
        }
      };

      client.onstatechange = (connectionState: number) => {
        if (resolved) return;

        if (connectionState === GuacamoleClientState.CONNECTED) {
          // Connection established successfully
          guacClientRef.current = client;
          retryCountRef.current = 0;
          lastRetryReasonRef.current = '';
          isReconnectingRef.current = false;
          isConnected = true;

          if (resolveConnected) resolveConnected();
          // Track activity by monitoring tunnel instructions
          const originalOnInstruction = tunnel.oninstruction;
          tunnel.oninstruction = function (opcode: string, parameters: string[]) {
            updateLastActivity();
            if (originalOnInstruction) {
              return originalOnInstruction.call(this, opcode, parameters);
            }
          };

          // Handle tunnel close events during active session
          if ('onclose' in tunnel) {
            // biome-ignore lint/suspicious/noExplicitAny: Guacamole types are incomplete
            const originalOnClose = (tunnel as any).onclose;
            // biome-ignore lint/suspicious/noExplicitAny: Guacamole types are incomplete
            (tunnel as any).onclose = () => {
              const errorReason = 'WebSocket tunnel closed during session';
              triggerReconnection(errorReason);

              if (originalOnClose) {
                originalOnClose.call(tunnel);
              }
            };
          }

          // Handle tunnel errors during active session
          const originalTunnelError = tunnel.onerror;
          tunnel.onerror = (status: unknown) => {
            const errorReason = `Tunnel error during session: ${formatError(status)}`;
            triggerReconnection(errorReason);

            if (originalTunnelError) {
              // biome-ignore lint/suspicious/noExplicitAny: Guacamole tunnel error type is unknown
              originalTunnelError.call(tunnel, status as any);
            }
          };

          // Set up mid-session client state change handler
          client.onstatechange = (clientState: number) => {
            updateLastActivity();

            setState((prev) => ({
              ...prev,
              sessionStatus: clientState as GuacamoleClientState,
            }));

            // Handle disconnection during active session
            if (clientState === GuacamoleClientState.DISCONNECTED) {
              const errorReason = 'Client state changed to DISCONNECTED during session';
              triggerReconnection(errorReason);
            }
          };

          // Handle client errors during active session
          client.onerror = (status: unknown) => {
            const errorReason = `Client error during session: ${formatError(status)}`;
            triggerReconnection(errorReason);
          };
        }
        if (connectionState === GuacamoleClientState.DISCONNECTED) {
          setState((prev) => ({
            ...prev,
            sessionStatus: GuacamoleClientState.DISCONNECTED,
          }));
          cleanup();
        }
      };

      // Handle client errors during initial connection
      client.onerror = (status: unknown) => {
        const errorReason = `Client error during initial connection: ${formatError(status)}`;
        lastRetryReasonRef.current = errorReason;
        cleanup();
        return { connected: false, isAcknowledged };
      };

      // Handle tunnel errors during initial connection
      tunnel.onerror = (status: unknown) => {
        const errorReason = `Tunnel error during initial connection: ${formatError(status)}`;
        lastRetryReasonRef.current = errorReason;
        cleanup();
        return { connected: false, isAcknowledged };
      };

      // Set up tunnel lifecycle handlers
      if ('onopen' in tunnel) {
        // biome-ignore lint/suspicious/noExplicitAny: Guacamole types are incomplete
        (tunnel as any).onopen = () => {
          updateLastActivity();
        };
      }

      if ('onclose' in tunnel) {
        // biome-ignore lint/suspicious/noExplicitAny: Guacamole types are incomplete
        (tunnel as any).onclose = () => {
          if (!resolved) {
            cleanup();
          }
          return { connected: false, isAcknowledged: false };
        };
      }

      // Attempt to establish the connection
      try {
        if (parameters) {
          client.connect(parameters);
        } else {
          client.connect();
        }
        await Promise.all([ackWaitPromise, connectionPromise]);
        return { connected: isConnected, isAcknowledged };
      } catch (error) {
        cleanup();
        return { connected: false, isAcknowledged: false };
      }
    },
    [triggerReconnection, updateLastActivity, formatError],
  );

  // Attempt to establish WebSocket connection with Guacamole
  const attemptWebSocketConnection = useCallback(async () => {
    if (guacClientRef.current) {
      return;
    }

    setState((prev) => ({
      ...prev,
      isConnecting: true,
      error: null,
      websocketFailed: false,
      sessionStatus: GuacamoleClientState.CONNECTING,
    }));

    try {
      const urlResult = await attemptWebSocketExtraction();

      if (!urlResult.success) {
        setState((prev) => ({
          ...prev,
          isConnecting: false,
          websocketFailed: true,
          error: `WebSocket URL extraction failed: ${urlResult.error}`,
          sessionStatus: GuacamoleClientState.DISCONNECTED,
        }));
        return;
      }

      const tunnel = new Guacamole.WebSocketTunnel(urlResult.url);
      const { connected, isAcknowledged } = await setupGuacamoleClient(tunnel, urlResult.parameters);

      if (connected && isAcknowledged) {
        setState((prev) => ({
          ...prev,
          isConnected: true,
          isAcknowledged: true,
          isConnecting: false,
          sessionStatus: GuacamoleClientState.CONNECTED,
        }));
        onSuccessfulConnection?.();
        startHeartbeatMonitoring();
      } else {
        // Connection failed - check if we should retry
        if (shouldRetry()) {
          isReconnectingRef.current = false;
          const errorReason = lastRetryReasonRef.current || 'WebSocket connection setup failed';
          setState((prev) => ({
            ...prev,
            isConnecting: false,
            error: `Connection attempt ${retryCountRef.current}/${MAX_RETRY_ATTEMPTS} failed`,
            sessionStatus: GuacamoleClientState.DISCONNECTED,
          }));

          triggerReconnection(errorReason);
        } else {
          setState((prev) => ({
            ...prev,
            isConnecting: false,
            websocketFailed: true,
            error: 'Connection failed after 3 attempts',
            sessionStatus: GuacamoleClientState.DISCONNECTED,
          }));
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorReason = `Connection attempt exception: ${errorMessage}`;
      isReconnectingRef.current = false;

      if (shouldRetry()) {
        setState((prev) => ({
          ...prev,
          isConnecting: false,
          error: errorMessage,
          sessionStatus: GuacamoleClientState.DISCONNECTED,
        }));
        triggerReconnection(errorReason);
      } else {
        setState((prev) => ({
          ...prev,
          isConnecting: false,
          websocketFailed: true,
          error: errorMessage,
          sessionStatus: GuacamoleClientState.DISCONNECTED,
        }));
      }
    }
  }, [
    attemptWebSocketExtraction,
    setupGuacamoleClient,
    shouldRetry,
    triggerReconnection,
    onSuccessfulConnection,
    startHeartbeatMonitoring,
  ]);

  useEffect(() => {
    connectionFunctionRef.current = attemptWebSocketConnection;
  }, [attemptWebSocketConnection]);

  // Handle network state changes for automatic reconnection
  useEffect(() => {
    const handleOnline = () => {
      if (!state.isConnected && !state.isConnecting && shouldRetry() && connectionFunctionRef.current) {
        connectionFunctionRef.current();
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [state.isConnected, state.isConnecting, shouldRetry]);

  // Main effect: initiate connection when remoteUrl changes and clean up on unmount
  useEffect(() => {
    if (!remoteUrl) return;

    if (guacClientRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (connectionFunctionRef.current) {
        connectionFunctionRef.current();
      }
    }, 1000);

    return () => {
      clearTimeout(timeoutId);

      // Clean up all timers and intervals
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      if (connectionHealthTimeoutRef.current) {
        clearTimeout(connectionHealthTimeoutRef.current);
        connectionHealthTimeoutRef.current = null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      // Reset retry state
      retryCountRef.current = 0;
      lastRetryReasonRef.current = '';
      isReconnectingRef.current = false;

      // Clean up Guacamole client
      if (guacClientRef.current) {
        try {
          const client = guacClientRef.current;
          client.onstatechange = null;
          client.onerror = null;
          client.disconnect();
        } catch (error) {}
        guacClientRef.current = null;
      }

      // Reset component state
      setState({
        isConnected: false,
        isConnecting: false,
        isAcknowledged: false,
        websocketFailed: false,
        error: null,
        sessionStatus: GuacamoleClientState.IDLE,
      });
    };
  }, [remoteUrl]);

  return {
    guacClient: guacClientRef.current,
    isConnected: state.isAcknowledged,
    isConnecting: state.isConnecting,
    isAcknowledged: state.isAcknowledged,
    websocketFailed: state.websocketFailed,
    error: state.error,
    sessionStatus: state.sessionStatus,
  };
};
